import { useContext, useState, useEffect, useRef } from 'preact/hooks';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import PollWinner from './ResultsScreen/pollWinner.component';
import Countdown from '../Countdown/Countdown.component';
import PollTotalVotes from '../PollTotalVotes/pollTotalVotes.component';

import { PollContext } from '../../potg/potg.component';

import { getImageUrl, onErrorImage } from '../../helpers/images';
import {
    getOptionById,
    getUserVote,
    getVotePercentages,
    sortPollOptions
} from '../../potg/potg.helpers';

import MVPIcon from '../shared-components/icons/MVPIcon';
import PenIcon from '../shared-components/icons/PenIcon';

import pollStyleSheet from './poll.module.scss';
import styleSheetButton from '../shared-components/button/scss/submitButton.module.scss';
const pollStyles = pollStyleSheet.locals || {};
const stylesButton = styleSheetButton.locals || {};

/**
 * Component to show poll results during or after a poll
 * The component handles both live results and final results
 *
 * @param {object} props poll option props
 * @param {string} props.transitionState transition state
 * @returns {Function} <PollResultsScreen />
 */
function PollResultsScreen({ transitionState }) {
    const {
        poll,
        pollOpen = false,
        transitionStyles,
        totalVotes = 0,
        showVotesCounter = false,
        onCountdownEnd,
        winningVotes,
        setPollUserAnswer
    } = useContext(PollContext);
    const { t } = useTranslation();

    const [expandResults, setExpandResults] = useState(false);

    // Current user vote
    const userVote = getOptionById(poll, getUserVote(poll.poll_id));

    // Options with percentages & simple copy of poll object (required for functions)
    const sortedPollOptions = sortPollOptions(
        poll,
        'times_selected',
        'desc',
        true
    );
    const optionsWithPercentage = [
        ...getVotePercentages(null, sortedPollOptions)
    ];

    const isClosed = !pollOpen;

    const winnerRef = useRef(null);
    const yourVoteRef = useRef(null);

    useEffect(() => {
        if (winnerRef.current) {
            const height = winnerRef.current.clientHeight;
            const heightCorrection = 8;
            document.documentElement.style.setProperty(
                '--left-col-height',
                `${height + heightCorrection}px`
            );
        }
        if (yourVoteRef.current) {
            const height = yourVoteRef.current.clientHeight;
            document.documentElement.style.setProperty(
                '--left-col-height',
                `${height}px`
            );
        }
    }, []);

    const voteItems = optionsWithPercentage.map((option) => (
        <li
            className={classNames(pollStyles.pollResultsListItem, {
                [pollStyles.pollResultsListItemWinner]:
                    winningVotes.includes(option) && isClosed,
                [pollStyles.pollResultsListItemFirst]:
                    winningVotes.includes(option)
            })}
            key={option.option_id}
        >
            {winningVotes.includes(option) && isClosed && (
                <div
                    className={pollStyles.pollResultsListItemWinnerIconWrapper}
                >
                    <MVPIcon
                        className={pollStyles.pollResultsListItemWinnerIcon}
                    />
                    <span
                        className={pollStyles.pollResultsListItemWinnerIconText}
                    >
                        1
                    </span>
                </div>
            )}
            <div
                className={classNames(pollStyles.pollResultsListItemInner, {
                    [pollStyles.pollResultsListItemInnerYourVote]:
                        userVote?.option_id === option.option_id
                })}
            >
                <div>
                    {option.external_image_url && !option.image_url && (
                        <img
                            src={option.external_image_url}
                            alt={`${option.text}`}
                            width="70"
                            onError={onErrorImage}
                            className={pollStyles.pollResultsListItemImg}
                        />
                    )}
                    {option.image_url && (
                        <img
                            src={getImageUrl(option.image_url, 70, 52)} // eslint-disable-line no-magic-numbers
                            alt={`${option.text}`}
                            width="70"
                            className={pollStyles.pollResultsListItemImg}
                        />
                    )}
                </div>
                <div className={pollStyles.pollResultsMeta}>
                    <div>{option.text}</div>
                    <div className={pollStyles.pollResultsPercentageBar}>
                        <div
                            style={{ width: `${option.percentage}%` }}
                            className={pollStyles.pollResultsPercentageBarFill}
                        />
                    </div>
                    <div>
                        <span className={pollStyles.pollResultsMetaPercentage}>
                            {option.percentage}%
                        </span>{' '}
                        {showVotesCounter && (
                            <span className={pollStyles.pollResultsMetaVotes}>
                                {option.times_selected} {t('common.votes')}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </li>
    ));

    return (
        <div part="potg-results" id="poll-results-screen">
            <style>{pollStyleSheet.toString()}</style>

            <div
                part="potg-results-wrapper"
                className={classNames(pollStyles.pollResultsWrapper, {
                    [pollStyles.pollResultsWrapperClosed]: isClosed
                })}
            >
                {isClosed ? (
                    <PollWinner
                        winningVotes={winningVotes}
                        userVote={userVote}
                        showCount={showVotesCounter}
                        ref={winnerRef}
                    />
                ) : (
                    <div className={pollStyles.pollYourVote} ref={yourVoteRef}>
                        <span className={pollStyles.pollHeadlineSmall}>
                            {t('polling.votedFor')}
                        </span>
                        {!userVote?.image_url &&
                            userVote?.external_image_url && (
                                <img
                                    src={userVote?.external_image_url}
                                    alt={`${userVote?.text}`}
                                    onError={onErrorImage}
                                    width="216"
                                />
                            )}
                        {/* eslint-disable no-magic-numbers */}
                        {userVote?.image_url && (
                            <img
                                src={getImageUrl(userVote?.image_url, 216, 162)}
                                alt={`${userVote?.text}`}
                                width="216"
                            />
                        )}
                        {/* eslint-enable no-magic-numbers */}
                        <div
                            className={classNames(pollStyles.pollYourVoteText, {
                                [pollStyles.pollYourVoteTextNoImage]:
                                    !userVote?.image_url &&
                                    !userVote?.external_image_url
                            })}
                        >
                            {userVote?.text}
                        </div>
                        <button
                            onClick={() => setPollUserAnswer(null)}
                            className={pollStyles.pollChangeVote}
                            data-testid="changeVoteButton"
                        >
                            {t('polling.changeVote')}
                            <PenIcon
                                className={pollStyles.pollChangeVoteIcon}
                            />
                        </button>
                    </div>
                )}

                <div
                    className={classNames(pollStyles.pollResults, {
                        [pollStyles.pollResultsClosed]: isClosed,
                        [pollStyles.pollResultsExpanded]: expandResults
                    })}
                >
                    {!expandResults && (
                        <div
                            className={classNames(
                                pollStyles.pollResultsShowMore,
                                {
                                    [pollStyles.pollResultsShowMoreClosed]:
                                        isClosed,
                                    [pollStyles.pollResultsExpanded]:
                                        expandResults
                                }
                            )}
                        >
                            <style>{styleSheetButton.toString()}</style>
                            <button
                                data-cy="expandResults"
                                className={classNames(
                                    stylesButton.submitButton
                                )}
                                onClick={() => setExpandResults(true)}
                                title="Expand full results"
                            >
                                {t('polling.expandButton')}
                            </button>
                        </div>
                    )}
                    <span
                        className={classNames(pollStyles.pollHeadlineSmall, {
                            [pollStyles.pollResultsHeadlineClosed]: isClosed,
                            [pollStyles.pollResultsExpanded]: expandResults
                        })}
                    >
                        {t('polling.liveStandings')}
                    </span>
                    <ol
                        className={classNames(pollStyles.pollResultsList, {
                            [pollStyles.pollResultsListClosed]: isClosed,
                            [pollStyles.pollResultsExpanded]: expandResults
                        })}
                    >
                        {voteItems}
                    </ol>
                </div>
            </div>

            {showVotesCounter && (
                <PollTotalVotes
                    votes={totalVotes}
                    margin={true}
                    style={{
                        ...transitionStyles[transitionState]
                    }}
                />
            )}

            {pollOpen && poll.closes_at && (
                <Countdown
                    text={t('polling.closesIn')}
                    datetime={poll.closes_at}
                    onCountdownEnd={onCountdownEnd}
                    closes={true}
                />
            )}
        </div>
    );
}

PollResultsScreen.propTypes = {
    transitionState: PropTypes.string.isRequired
};

export default PollResultsScreen;
